import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { selectUser } from "../../features/userSlice";
import { useNavigate } from "react-router-dom";
import { FaTrash, FaEdit } from "react-icons/fa";
import styles from "./VehicleTagIssue.module.css";
import FloatingActions from "../../components/FloatingActions";

const VehicleTagIssue = () => {
  const user = useSelector(selectUser);
  const token = user?.token;
  const emp_id = user?.emp_id;

  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [cards, setCards] = useState([]);
  const [toast, setToast] = useState("");
  const [popup, setPopup] = useState({ open: false, title: "", message: "" });

  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);

  const [scrollPct, setScrollPct] = useState(0);

  const topRef = useRef(null);
  const bottomRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();

  const showPopup = (message, title = "Notice") =>
    setPopup({ open: true, title, message });
  const closePopup = () => setPopup({ open: false, title: "", message: "" });

  // Parse comma-separated search tokens
  const tokens = useMemo(() => {
    return (query || "")
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
  }, [query]);

  const fetchOneToken = async (vehicleNoPart) => {
    const res = await axios.post("/api/wbauto/getTruckUHFTags", {
      emp_id,
      token,
      vehicleNo: vehicleNoPart,
    });
    return Array.isArray(res.data) ? res.data : [];
  };

  const fetchTags = async () => {
    if (!token) return;
    try {
      setLoading(true);
      let results = [];
      if (tokens.length === 0) {
        results = await fetchOneToken("");
      } else {
        const chunks = await Promise.all(tokens.map(fetchOneToken));
        const merged = new Map();
        chunks.flat().forEach((r) => {
          const key = String(r.VehicleNo || "").toUpperCase();
          if (!merged.has(key)) merged.set(key, r);
        });
        results = Array.from(merged.values());
      }
      setCards(results);
      // reset scroll indicators on new search
      setScrollPct(0);
      if (listRef.current) listRef.current.scrollTop = 0;
    } catch (err) {
      console.error("getTruckUHFTags error:", err);
      showPopup(
        err?.response?.data?.message || "Failed to load UHF tags.",
        "Error"
      );
      setCards([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (vehicleNo) => {
    if (!vehicleNo) return;
    const ok = window.confirm(
      `Delete UHF tag record for vehicle: ${vehicleNo}?`
    );
    if (!ok) return;

    try {
      setLoading(true);
      const res = await axios.post("/api/wbauto/deleteTruckUHFTags", {
        emp_id,
        token,
        vehicleNo,
      });
      if (res.data?.success) {
        setToast("✅ Deleted successfully.");
        await fetchTags(); // refresh
      } else {
        const msg = res.data?.message || "Failed to delete.";
        setToast(`❌ ${msg}`);
        showPopup(msg, "Error");
      }
    } catch (err) {
      console.error("deleteTruckUHFTags error:", err);
      const msg = err?.response?.data?.message || "Error deleting record.";
      setToast(`❌ ${msg}`);
      showPopup(msg, "Error");
    } finally {
      setLoading(false);
      topRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const openEdit = (row) => {
    setEditRow({
      ...row,
      Date: toLocalInput(row.Date),
      updateDate: toLocalInput(row.updateDate),
      GatescanDate: toLocalInput(row.GatescanDate),
    });
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditRow(null);
    setEditOpen(false);
  };

  const handleSaveEdit = async () => {
    if (!editRow?.VehicleNo) {
      showPopup("VehicleNo is missing.", "Error");
      return;
    }
    try {
      setLoading(true);

      const payload = {
        emp_id,
        token,
        data: {
          VehicleNo: editRow.VehicleNo,
          UHF1: editRow.UHF1 ?? "",
          UHF2: editRow.UHF2 ?? "",
          UHF3: editRow.UHF3 ?? "",
          UHF4: editRow.UHF4 ?? "",
          UHF5: editRow.UHF5 ?? "",
          Active: editRow.Active ?? "",
          Date: fromLocalInputToISO(editRow.Date),
          updateDate: fromLocalInputToISO(editRow.updateDate),
          GatescanDate: fromLocalInputToISO(editRow.GatescanDate),
        },
      };

      const res = await axios.post(
        "/api/wbauto/updateTruckUHFTags",
        payload
      );
      if (res.data?.success) {
        setToast("✅ Updated successfully.");
        closeEdit();
        await fetchTags(); // refresh
        topRef.current?.scrollIntoView({ behavior: "smooth" });
      } else {
        const msg = res.data?.message || "Failed to update.";
        setToast(`❌ ${msg}`);
        showPopup(msg, "Error");
      }
    } catch (err) {
      console.error("updateTruckUHFTags error:", err);
      const msg = err?.response?.data?.message || "Error updating record.";
      setToast(`❌ ${msg}`);
      showPopup(msg, "Error");
    } finally {
      setLoading(false);
    }
  };

  // initial load
  useEffect(() => {
    fetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 2500);
    return () => clearTimeout(t);
  }, [toast]);

  // Scroll progress chip
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const onScroll = () => {
      const max = el.scrollHeight - el.clientHeight;
      const pct = max > 0 ? Math.min(100, Math.round((el.scrollTop / max) * 100)) : 0;
      setScrollPct(pct);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [cards.length]);

  const scrollTop = () => topRef.current?.scrollIntoView({ behavior: "smooth" });
  const scrollBottom = () => bottomRef.current?.scrollIntoView({ behavior: "smooth" });

  if (!token) {
    return (
      <div className={styles.approveContainer}>
        <p>You must be logged in to manage vehicle tags.</p>
        <button className={styles.cancelBtn} onClick={() => navigate("/")}>
          Go to Login
        </button>
      </div>
    );
  }

  return (
    <div className={styles.approveContainer}>
      <div ref={topRef} />
      <h2 className={styles.screenTitle}>Vehicle UHF Tag Issue</h2>

      {toast && <div className={styles.toast}>{toast}</div>}

      {popup.open && (
        <div className={styles.modalBackdrop} onClick={closePopup}>
          <div className={styles.modalSheet} onClick={(e) => e.stopPropagation()}>
            <h4 className={styles.modalTitle}>{popup.title}</h4>
            <p className={styles.modalBody}>{popup.message}</p>
            <button className={styles.modalCloseBtn} onClick={closePopup}>OK</button>
          </div>
        </div>
      )}

      {/* Search */}
      <div className={styles.filters}>
        <label>Vehicle No (comma separated):</label>
        <input
          className={styles.input}
          type="text"
          placeholder="e.g. 819, AP05X1234"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") fetchTags(); }}
        />
        <button className={styles.fetchBtn} onClick={fetchTags} disabled={loading}>
          {loading ? "Searching..." : "Search"}
        </button>
        {/* Result meta */}
        <div className={styles.resultMeta}>
          {loading ? "Searching…" : `${cards.length} result${cards.length === 1 ? "" : "s"}`}
        </div>
      </div>

      {/* Results */}
      <div className={styles.cardsScroll} ref={listRef}>
        {/* Scroll chip */}
        <div className={styles.scrollChip}>
          {cards.length} {cards.length === 1 ? "result" : "results"} • {scrollPct}%
        </div>

        {loading && <div className={styles.noRecords}>Loading…</div>}

        {!loading && cards.length === 0 && (
          <div className={styles.noRecords}>No records found.</div>
        )}

        {!loading && cards.length > 0 && cards.map((row) => (
          <div key={row.VehicleNo} className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.avatar}>
                {String(row.VehicleNo || "?").charAt(0).toUpperCase()}
              </div>
              <div className={styles.headerText}>
                <div className={styles.title}>{row.VehicleNo || "(unknown)"}</div>
                <div className={styles.subTitle}>
                  Active: {row.Active ?? "-"} &nbsp;|&nbsp; Issued:{" "}
                  {/* {row.Date ? new Date(row.Date).toLocaleString() : "-"} */}
                  {row.Date ? formatUTCDate(row.Date) : "-"}
                </div>
              </div>
              <div className={styles.cardActions}>
                <button className={styles.iconBtn} title="Edit" onClick={() => openEdit(row)}>
                  <FaEdit />
                </button>
                <button
                  className={`${styles.iconBtn} ${styles.danger}`}
                  title="Delete"
                  onClick={() => handleDelete(row.VehicleNo)}
                >
                  <FaTrash />
                </button>
              </div>
            </div>

            <div className={styles.cardBody}>
              <div className={styles.row}><span>UHF1</span><strong>{row.UHF1 || "-"}</strong></div>
              <div className={styles.row}><span>UHF2</span><strong>{row.UHF2 || "-"}</strong></div>
              <div className={styles.row}><span>UHF3</span><strong>{row.UHF3 || "-"}</strong></div>
              <div className={styles.row}><span>UHF4</span><strong>{row.UHF4 || "-"}</strong></div>
              <div className={styles.row}><span>UHF5</span><strong>{row.UHF5 || "-"}</strong></div>
              <div className={styles.row}>
                <span>Updated</span>
                {/* <strong>{row.updateDate ? new Date(row.updateDate).toLocaleString() : "-"}</strong> */}
                <strong>{formatUTCDate(row.updateDate)}</strong>
              </div>
              <div className={styles.row}>
                <span>Gate Scan</span>
                {/* <strong>{row.GatescanDate ? new Date(row.GatescanDate).toLocaleString() : "-"}</strong> */}
                <strong>{formatUTCDate(row.GatescanDate)}</strong>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Edit sheet */}
      {editOpen && editRow && (
        <div className={styles.modalBackdrop} onClick={closeEdit}>
          <div
            className={`${styles.modalSheet} ${styles.modalSheetWide}`}
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className={styles.modalTitle}>Edit UHF Tags – {editRow.VehicleNo}</h4>

            <div className={styles.formGrid}>
              {/* Text fields */}
              {["UHF1", "UHF2", "UHF3", "UHF4", "UHF5"].map((f) => (
                <div key={f} className={styles.formItem}>
                  <label>{f}</label>
                  <input
                    type="text"
                    className={styles.input}
                    value={editRow[f] ?? ""}
                    onChange={(e) => setEditRow((prev) => ({ ...prev, [f]: e.target.value }))}
                  />
                </div>
              ))}

              {/* Active as select */}
              <div className={styles.formItem}>
                <label>Active</label>
                <select
                  className={styles.input}
                  value={String(editRow.Active ?? "")}
                  onChange={(e) => setEditRow((p) => ({ ...p, Active: e.target.value }))}
                >
                  <option value="">(blank)</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>

              {/* Datetime fields */}
              <div className={styles.formItem}>
                <label>Issue Date</label>
                <input
                  type="datetime-local"
                  className={styles.input}
                  value={editRow.Date || ""}
                  onChange={(e) => setEditRow((p) => ({ ...p, Date: e.target.value }))}
                />
              </div>

              <div className={styles.formItem}>
                <label>Update Date</label>
                <input
                  type="datetime-local"
                  className={styles.input}
                  value={editRow.updateDate || ""}
                  onChange={(e) => setEditRow((p) => ({ ...p, updateDate: e.target.value }))}
                />
              </div>

              <div className={styles.formItem}>
                <label>Gate Scan Date</label>
                <input
                  type="datetime-local"
                  className={styles.input}
                  value={editRow.GatescanDate || ""}
                  onChange={(e) => setEditRow((p) => ({ ...p, GatescanDate: e.target.value }))}
                />
              </div>
            </div>

            <div className={styles.modalActions}>
              <button className={styles.cancelBtn} onClick={closeEdit}>Close</button>
              <button className={styles.approveBtn} onClick={handleSaveEdit} disabled={loading}>
                {loading ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating: Top • Bottom • Dashboard */}
      <FloatingActions topRef={topRef} bottomRef={bottomRef} />
      <div ref={bottomRef} />
    </div>
  );
};

/* Helpers: datetime-local formatting */
function toLocalInput(date) {
  if (!date) return "";
  const d = new Date(date);
  if (isNaN(d)) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}
function fromLocalInputToISO(v) {
  if (!v) return "";
  const d = new Date(v);
  return isNaN(d) ? "" : d.toISOString();
}

const formatUTCDate = (date) => {
    if (!date) return "-";

    return new Intl.DateTimeFormat("en-IN", {
        timeZone: "UTC",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
    }).format(new Date(date));
};

export default VehicleTagIssue;
